import React, { useMemo, useState } from "react"
import { dateFmt, fmt } from "../helpers/format"

const PAGE_SIZE = 15

const KINDS = [
  { key: "quotes",            dot: "bg-violet-500" },
  { key: "invoices",          dot: "bg-emerald-500" },
  { key: "purchase_orders",   dot: "bg-amber-500" },
  { key: "purchase_invoices", dot: "bg-blue-500" },
]

const TONES = {
  positive: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  info:     "bg-blue-50 text-blue-700 ring-blue-200",
  warning:  "bg-amber-50 text-amber-700 ring-amber-200",
  negative: "bg-rose-50 text-rose-700 ring-rose-200",
  neutral:  "bg-slate-100 text-slate-700 ring-slate-200",
}

const totalOf = items => {
  const cents  = items.reduce((sum, item) => sum + (item.amount?.cents || 0), 0)
  const symbol = items.find(item => item.amount?.symbol)?.amount.symbol || "€"
  return `${(cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`
}

const t = (key, options) => I18n.t(`projects.documents.${key}`, options)

const Chip = ({ tone, children }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset whitespace-nowrap ${TONES[tone] || TONES.neutral}`}>
    {children}
  </span>
)

const Tile = ({ kind, items, active, onClick }) => {
  const waiting = items.filter(item => item.tone === "warning" || item.tone === "info").length

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`text-left bg-white rounded-xl px-4 py-3 border transition-colors ${active ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200 hover:border-slate-300"}`}
    >
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${kind.dot}`} />
        <span className="text-[10.5px] uppercase tracking-wider font-medium text-slate-500">{t(`cards.${kind.key}`)}</span>
      </div>
      <div className="text-[18px] font-semibold tabular-nums text-slate-900 mt-1">{totalOf(items)}</div>
      <div className="text-[11.5px] text-slate-500 mt-0.5">
        {t("count", { count: items.length })}
        {waiting > 0 && <span className="text-slate-400"> · {t(`waiting.${kind.key}`, { count: waiting })}</span>}
      </div>
    </button>
  )
}

const Row = ({ item }) => (
  <tr className="group hover:bg-slate-50/70">
    <td className="pl-4 pr-2 py-2.5 align-top">
      <span className="inline-flex items-center gap-2 text-[11.5px] text-slate-500 whitespace-nowrap">
        <span className={`w-2 h-2 rounded-full ${item.kind.dot}`} />
        {t(`kinds.${item.kind.key}`)}
      </span>
    </td>
    <td className="px-2 py-2.5 align-top min-w-0">
      <a href={item.path} className="block text-[12.5px] font-medium text-slate-900 group-hover:text-blue-700 truncate max-w-[420px]" title={item.title}>
        {item.title}
      </a>
      {item.reference && <div className="text-[11px] text-slate-500 tabular-nums">{item.reference}</div>}
    </td>
    <td className="px-2 py-2.5 align-top text-[12px] text-slate-700 truncate max-w-[220px]" title={item.party || ""}>{item.party || "—"}</td>
    <td className="px-2 py-2.5 align-top text-[12px] text-slate-600 whitespace-nowrap tabular-nums">{dateFmt(item.date)}</td>
    <td className="px-2 py-2.5 align-top">
      <div className="flex flex-wrap gap-1">
        {item.status_label && <Chip tone={item.tone}>{item.status_label}</Chip>}
        {item.alert && <Chip tone="negative">{item.alert.label} +{fmt(item.alert.amount)}</Chip>}
      </div>
    </td>
    <td className="px-2 py-2.5 align-top text-right text-[12.5px] font-medium text-slate-900 tabular-nums whitespace-nowrap">{fmt(item.amount)}</td>
    <td className="pl-2 pr-4 py-2.5 align-top text-right whitespace-nowrap">
      {item.pdf_path && (
        <a href={item.pdf_path} target="_blank" rel="noopener" className="text-[11.5px] font-medium text-slate-500 hover:text-blue-700" title={t("open_pdf")}>
          PDF
        </a>
      )}
    </td>
  </tr>
)

const DocumentsGrid = ({ documents }) => {
  const [kindKey, setKindKey] = useState(null)
  const [query, setQuery]     = useState("")
  const [shown, setShown]     = useState(PAGE_SIZE)

  const all = useMemo(() =>
    KINDS.flatMap(kind => (documents[kind.key] || []).map(item => ({ ...item, kind })))
      .sort((a, b) => String(b.date || "").localeCompare(String(a.date || ""))),
  [documents])

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return all.filter(item =>
      (!kindKey || item.kind.key === kindKey) &&
      (!needle || [item.title, item.reference, item.party, item.status_label].some(v => String(v || "").toLowerCase().includes(needle)))
    )
  }, [all, kindKey, query])

  const visible   = rows.slice(0, shown)
  const remaining = rows.length - visible.length

  const pickKind = key => { setKindKey(current => (current === key ? null : key)); setShown(PAGE_SIZE) }

  return (
    <section id="section-documents" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">{t("title")}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{t("subtitle")}</p>
        </div>
        <input
          type="search"
          value={query}
          onChange={event => { setQuery(event.target.value); setShown(PAGE_SIZE) }}
          placeholder={t("search")}
          className="w-80 h-8 px-3 text-[12.5px] bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400"
        />
      </header>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        {KINDS.map(kind => (
          <Tile key={kind.key} kind={kind} items={documents[kind.key] || []} active={kindKey === kind.key} onClick={() => pickKind(kind.key)} />
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between text-[12px]">
          <span className="text-slate-600">
            {kindKey ? t(`cards.${kindKey}`) : t("all")}
            <span className="text-slate-400"> · {t("count", { count: rows.length })}</span>
            {kindKey && (
              <button type="button" onClick={() => pickKind(kindKey)} className="ml-2 text-blue-600 hover:text-blue-700">{t("clear_filter")}</button>
            )}
          </span>
          {/* Quotes, sales and purchases do not add up: a total only reads within one kind. */}
          {kindKey && <span className="text-slate-500">{t("total_ht")} <span className="font-semibold text-slate-900 tabular-nums">{totalOf(rows)}</span></span>}
        </div>

        {rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-[12.5px] text-slate-500">{query.trim() ? t("no_results") : t("empty")}</div>
        ) : (
          <table className="w-full table-auto">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="pl-4 pr-2 py-2 text-left font-medium">{t("columns.kind")}</th>
                <th className="px-2 py-2 text-left font-medium">{t("columns.document")}</th>
                <th className="px-2 py-2 text-left font-medium">{t("columns.party")}</th>
                <th className="px-2 py-2 text-left font-medium">{t("columns.date")}</th>
                <th className="px-2 py-2 text-left font-medium">{t("columns.status")}</th>
                <th className="px-2 py-2 text-right font-medium">{t("columns.amount")}</th>
                <th className="pl-2 pr-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map(item => <Row key={`${item.kind.key}-${item.id}`} item={item} />)}
            </tbody>
          </table>
        )}

        {remaining > 0 && (
          <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-center gap-4 text-[12px]">
            <button type="button" onClick={() => setShown(n => n + PAGE_SIZE)} className="font-medium text-blue-600 hover:text-blue-700">
              {t("show_more", { count: Math.min(PAGE_SIZE, remaining) })}
            </button>
            <button type="button" onClick={() => setShown(rows.length)} className="text-slate-500 hover:text-slate-700">
              {t("show_all", { count: rows.length })}
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

export default DocumentsGrid
